<template>
  <ContentModal v-model="show" title="Stats" size="2xl">
    <div class="d-flex flex-wrap gap-2 align-items-end mb-4">
      <label class="form-label text-sm mb-0">From<input v-model="filters.start_date" type="date" class="form-control form-control-sm mt-1"></label>
      <label class="form-label text-sm mb-0">To<input v-model="filters.end_date" type="date" class="form-control form-control-sm mt-1"></label>
      <label class="form-label text-sm mb-0">Organization<select v-model="filters.organization_id" class="form-select form-select-sm mt-1"><option value="">All organizations</option><option v-for="organization in options.organizations" :key="organization.id" :value="organization.id">{{ organization.name }}</option></select></label>
      <label class="form-label text-sm mb-0">Location<select v-model="filters.location_id" class="form-select form-select-sm mt-1"><option value="">All locations</option><option v-for="location in options.locations" :key="location.id" :value="location.id">{{ location.name }}</option></select></label>
      <label class="form-label text-sm mb-0">Organizations<select v-model="filters.active_organizations" class="form-select form-select-sm mt-1"><option value="all">All</option><option value="active">Active only</option></select></label>
      <label class="form-label text-sm mb-0">Locations<select v-model="filters.active_locations" class="form-select form-select-sm mt-1"><option value="all">All</option><option value="active">Active only</option></select></label>
      <button type="button" class="btn btn-sm btn-outline-primary mb-0" :disabled="loading" @click="fetchStats"><i class="material-icons text-sm">filter_list</i></button>
    </div>
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-primary" role="status"></div></div>
    <div v-else-if="error" class="text-center py-4 text-danger">{{ error }}</div>
    <div v-else class="row g-4">
      <div class="col-lg-6"><section class="chart-section"><div class="d-flex justify-content-between"><h6>Assignment Response Methods</h6><span class="text-xs text-secondary">{{ stats?.total_responses || 0 }} responses</span></div><div v-if="stats?.response_methods?.length" class="chart-wrap"><canvas ref="responseCanvas"></canvas></div><p v-else class="text-center text-secondary py-5 mb-0">No responses in this date range.</p></section></div>
      <div class="col-lg-6"><section class="chart-section"><h6>Email Domains</h6><BaseTable v-if="stats?.email_domains?.length" heading="" :rows="stats.email_domains" :columns="domainColumns" idKey="domain" :searchable="true" :selectable="false" :initialSortKey="'count'" :initialSortOrder="'desc'" /><p v-else class="text-center text-secondary py-5 mb-0">No email addresses match these filters.</p></section></div>
    </div>
  </ContentModal>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'
import Chart from 'chart.js/auto'
import ContentModal from '@/components/Modal/ContentModal.vue'
import BaseTable from '@/components/Table/Table.vue'
import api from '@/config/api'

const props = defineProps({ modelValue: Boolean })
const emit = defineEmits(['update:modelValue'])
const show = computed({ get: () => props.modelValue, set: (value) => emit('update:modelValue', value) })
function dateInput(offset) { const date = new Date(); date.setDate(date.getDate() + offset); return date.toISOString().slice(0, 10) }
const filters = reactive({ start_date: dateInput(-34), end_date: dateInput(0), organization_id: '', location_id: '', active_organizations: 'all', active_locations: 'all' })
const options = reactive({ organizations: [], locations: [] })
const stats = ref(null)
const loading = ref(false)
const error = ref('')
const responseCanvas = ref(null)
let responseChart = null
const colors = ['#0ea5e9', '#22c55e', '#f59e0b', '#ec4899', '#6366f1', '#14b8a6', '#64748b']
const domainColumns = [
  { key: 'domain', label: 'DOMAIN' },
  { key: 'count', label: 'UNIQUE EMAILS' },
]

function destroyCharts() { if (responseChart) responseChart.destroy(); responseChart = null }
function makeChart(canvas, labels, values, tooltipRows) {
  return new Chart(canvas, { type: 'doughnut', data: { labels, datasets: [{ data: values, backgroundColor: colors, borderColor: '#fff', borderWidth: 2 }] }, options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } }, tooltip: { callbacks: { label: (context) => tooltipRows[context.dataIndex] } } } } })
}
function renderCharts() {
  destroyCharts()
  const responseRows = stats.value?.response_methods || []
  if (responseCanvas.value && responseRows.length) responseChart = makeChart(responseCanvas.value, responseRows.map((row) => row.label), responseRows.map((row) => row.count), responseRows.map((row) => `${row.label}: ${row.count} (${row.percent}%)`))
}
async function fetchStats() {
  loading.value = true; error.value = ''
  try {
    const response = await api.get('/admin/response-stats', { params: filters })
    stats.value = response.data?.data || null
    options.organizations = stats.value?.filters?.organizations || options.organizations
    options.locations = stats.value?.filters?.locations || options.locations
  } catch (exception) { error.value = exception.response?.data?.message || 'Failed to load statistics.' }
  finally {
    loading.value = false
    await nextTick()
    renderCharts()
  }
}
watch(show, (open) => { if (open) fetchStats(); else destroyCharts() })
onBeforeUnmount(destroyCharts)
</script>

<style scoped>
.form-label { min-width:132px; color:#475569; }
.chart-section { min-height:390px; padding:16px; border:1px solid #e2e8f0; border-radius:8px; }
.chart-section h6 { font-size:.9rem; font-weight:700; margin-bottom:14px; }
.chart-wrap { height:320px; position:relative; }
@media (max-width: 575.98px) { .form-label { width:calc(50% - .25rem); min-width:0; } .chart-wrap { height:270px; } }
</style>
